import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, signIn, signUp, isVerified, sendVerification, refreshVerification } from '../AuthContext';
import { auth } from '../firebaseConfig';
import { useMyOrgs, roleSatisfies } from '../data/entities';
import { appKit, platformFree, platformStart } from '../data/plans';
import { fetchAgreements, legalStatus, acceptLegal } from '../data/legal';
import { backendBase } from '../utils/backendApi';
import { track } from '../utils/track';
import Clickwrap from '../components/Clickwrap';
import OAuthButtons from '../components/OAuthButtons';
import OrgStart from '../components/OrgStart';

const PLANS = {
  free: {
    product: platformFree.product,
    price: platformFree.price,
    helper: platformFree.helper,
    nameLabel: 'Organization',
    nameHint: 'The workspace you build in. You can rename it later.',
    free: true,
  },
  start: {
    product: platformStart.product,
    price: platformStart.price,
    helper: platformStart.helper,
    nameLabel: 'Organization',
    nameHint: 'The workspace this subscription belongs to.',
  },
  kit: {
    product: appKit.product,
    price: appKit.price,
    helper: appKit.helper,
    nameLabel: 'App name',
    nameHint: 'Becomes the instance URL. You can change the name later.',
  },
};

function recall(plan, uid) {
  try {
    const v = JSON.parse(sessionStorage.getItem('nv-start') || 'null');
    if (v && v.plan === plan && v.uid === uid) return v;
  } catch { /* ignore */ }
  return null;
}

function remember(plan, uid, orgId, appId) {
  try { sessionStorage.setItem('nv-start', JSON.stringify({ plan, uid, orgId, appId: appId || null })); } catch { /* ignore */ }
}

async function postExpress(body) {
  const me = auth.currentUser;
  if (!me) throw new Error('not signed in');
  const token = await me.getIdToken();
  const res = await fetch(`${backendBase()}/billing/express`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.orgId = data?.orgId || null;
    err.appId = data?.appId || null;
    err.code = data?.code || null; // email-unverified | domain-owned (shared/signupPolicy.js)
    err.org = data?.org || null;
    throw err;
  }
  return data;
}

export default function StartCheckout() {
  const { user, loading } = useAuth();
  const orgs = useMyOrgs();
  const [params] = useSearchParams();
  const plan = params.get('plan');
  const offer = PLANS[plan] || null;
  const cancelled = params.get('checkout') === 'cancelled';
  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [freshOrg, setFreshOrg] = useState(false);
  const [docs, setDocs] = useState(null);
  const [docsErr, setDocsErr] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState(false); // password signup: link sent, order parked
  const [owned, setOwned] = useState(null); // 409 domain-owned: the org that verified this domain
  const [, bump] = useState(0);

  const admins = (orgs || []).filter((o) => roleSatisfies(o.myRole, 'admin'));
  const here = `/start?plan=${encodeURIComponent(plan || '')}`;

  // Back from the verification link (or the backfill ran): refresh the
  // cached record so the page stops saying "verify" and the token carries
  // email_verified for /billing/express.
  useEffect(() => {
    if (!user || isVerified(user)) return undefined;
    let alive = true;
    refreshVerification().then((ok) => { if (alive && ok) bump((n) => n + 1); });
    const t = setInterval(() => refreshVerification().then((ok) => { if (alive && ok) { clearInterval(t); bump((n) => n + 1); } }), 4000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    let alive = true;
    fetchAgreements()
      .then((list) => { if (alive) setDocs(list); })
      .catch((e) => { if (alive) setDocsErr(e.message); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!user || !offer) return undefined;
    const saved = recall(plan, user.uid);
    if (saved?.orgId) setOrgId((cur) => cur || saved.orgId);
    return undefined;
  }, [user, offer, plan]);

  useEffect(() => {
    if (!user || !offer) return undefined;
    let alive = true;
    legalStatus(orgId || '')
      .then((s) => { if (alive) setStatus(s); })
      .catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [user, offer, orgId]);

  useEffect(() => {
    if (!freshOrg && !orgId && admins[0]) setOrgId(admins[0].id);
  }, [freshOrg, orgId, admins]);

  useEffect(() => {
    if (!email || name) return;
    const local = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
    if (local) setName(local.slice(0, 40));
  }, [email, name]);

  const pay = async (seen) => {
    setError('');
    setBusy(true);
    try {
      if (!user) {
        if (!email.trim() || !password) throw new Error('Email and password are required.');
        if (mode === 'signup') await signUp(email.trim(), password);
        else await signIn(email.trim(), password);
      }
      const me = auth.currentUser;
      if (!me) throw new Error('Sign-in did not complete.');
      if (!user) track('signup_done', { plan });
      if (!isVerified(me)) {
        // A password account must prove the address before an organization
        // is created in its name (anyone can type cfo@acme.com). Record the
        // agreements now so the return trip is one click, send the link,
        // park the order.
        try { await acceptLegal({ scope: 'user', docIds: (seen || docs || []).map((d) => d.id) }); } catch { /* re-shown on return */ }
        await sendVerification(me, here);
        setVerifyNotice(true);
        setBusy(false);
        return;
      }
      const saved = recall(plan, me.uid);
      const chosen = orgId || saved?.orgId || '';
      if (!chosen && !name.trim()) throw new Error(`Name the ${plan === 'kit' ? 'app' : 'organization'}.`);
      const out = await postExpress({
        plan,
        name: name.trim(),
        orgId: chosen || undefined,
        appId: saved?.appId || undefined,
        ref: params.get('ref') || undefined,
        docIds: (seen || docs || []).map((d) => d.id),
        scrolled: true,
      });
      remember(plan, me.uid, out.orgId, out.appId);
      if (!chosen) track('org_created', { plan });
      track(offer.free ? 'console_open' : 'checkout_open', { plan });
      window.location.href = out.url;
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setMode('signin');
        setError('That email already has an account. Sign in, and the order stays here.');
      } else if (err.code === 'email-unverified') {
        setVerifyNotice(true);
      } else if (err.code === 'domain-owned' && err.org) {
        setOwned(err.org);
      } else {
        setError(err.message);
      }
      const me = auth.currentUser;
      if (me && (err.orgId || err.appId)) {
        remember(plan, me.uid, err.orgId, err.appId);
        if (err.orgId) setOrgId(err.orgId);
      }
      setBusy(false);
    }
  };

  useEffect(() => {
    if (offer) track('start_view', { plan });
  }, [offer, plan]);

  if (!offer) {
    return (
      <div className="cs-page cs-start-wrap">
        <h1>What are you starting?</h1>
        <div className="cs-start-pick">
          {[platformStart, appKit, platformFree].map((t) => (
            <Link key={t.id} to={t.href} className={`cs-card cs-start-pick-card${t.id === 'start' ? ' is-featured' : ''}`}>
              <strong>{t.product}</strong>
              <span>{t.price}</span>
              <p>{t.helper}</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const already = !!(user && status?.userOk && (!orgId || status?.orgOk));

  return (
    <div className="cs-page cs-start-wrap">
      <div className="cs-start">
        <aside className="cs-card cs-start-order">
          <p className="cs-start-kicker">Your order</p>
          <h1>{offer.product}</h1>
          <p className="cs-start-price">{offer.price}</p>
          <p className="cs-help">{offer.helper}</p>
          <p className="cs-help" style={{ marginTop: 10 }}>
            {offer.free
              ? 'No card. After the agreements you are in the console; upgrade on Billing when the first workflow is ready to run.'
              : 'One step after the agreements: Stripe Checkout. Apple Pay shows there when this device supports it.'}
          </p>
        </aside>
        <div className="cs-start-form">
          {cancelled && (
            <p className="cs-start-note">Payment was not completed. The order is still here.</p>
          )}
          {error && <p className="cs-start-err">{error}</p>}
          {verifyNotice && (
            <div className="cs-card" style={{ padding: 14, marginBottom: 14 }}>
              <strong style={{ color: 'var(--text)' }}>Check your inbox</strong>
              <p className="cs-help" style={{ marginTop: 6 }}>
                We sent a verification link to <strong style={{ color: 'var(--text)' }}>{user?.email || email}</strong>. Open it and this order
                continues from here — the page notices on its own. <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => sendVerification(auth.currentUser, here).catch((e) => setError(e.message))}>Resend</button>
              </p>
            </div>
          )}
          {owned && (
            <div className="cs-card" style={{ padding: 14, marginBottom: 14 }}>
              <strong style={{ color: 'var(--text)' }}>{owned.name} already uses nventr</strong>
              <p className="cs-help" style={{ marginTop: 6 }}>
                Your company has verified this email domain, so a new organization is not created in its name.
                Ask to join and an administrator approves you; plans and apps are bought from inside that organization.
              </p>
              <OrgStart compact onCreated={(id) => { setOwned(null); setOrgId(id); }} />
            </div>
          )}
          {loading ? <p className="cs-help">Loading…</p> : (
            <>
              {!user && !verifyNotice && <OAuthButtons onError={setError} disabled={busy} />}
              {!user && !verifyNotice && (
                <>
                  <div className="cs-field">
                    <label>Email</label>
                    <input
                      type="email"
                      className="cs-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div className="cs-field">
                    <label>Password</label>
                    <input
                      type="password"
                      className="cs-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                      minLength={6}
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(''); }}
                  >
                    {mode === 'signup' ? 'Have an account? Sign in' : 'Need an account? Create one'}
                  </button>
                </>
              )}
              {user && admins.length > 0 ? (
                <div className="cs-field">
                  <label>Organization</label>
                  <select className="cs-input" value={orgId} onChange={(e) => { setOrgId(e.target.value); setFreshOrg(!e.target.value); }}>
                    <option value="">New organization</option>
                    {admins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              ) : null}
              {(!user || !orgId) && (
                <div className="cs-field">
                  <label>{offer.nameLabel}</label>
                  <input
                    className="cs-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={120}
                  />
                  <p className="cs-help">{offer.nameHint}</p>
                </div>
              )}
              {docsErr && <p className="cs-start-err">{docsErr}</p>}
              {docs && !already && !verifyNotice && !owned && (
                <Clickwrap
                  docs={docs}
                  busy={busy}
                  error=""
                  intro={user
                    ? `Read to the end. Agreeing opens ${offer.free ? 'the console' : 'checkout'}.`
                    : `Read to the end. Agreeing creates the account and opens ${offer.free ? 'the console' : 'checkout'}.`}
                  onAgree={pay}
                />
              )}
              {docs && already && !owned && (
                <button type="button" className="cs-btn cs-btn-primary" disabled={busy || verifyNotice} onClick={() => pay(docs)} data-agent-noclick>
                  {busy ? 'Opening…' : (offer.free ? 'Open the console' : `Pay ${offer.price}`)}
                </button>
              )}
              {!docs && !docsErr && <p className="cs-help">Loading agreements…</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
